import { createContext, useContext, useEffect, useRef, useState } from "react";
import { displayTitle } from "@/lib/title";
import { trackAddToCart, trackRemoveFromCart } from "@/lib/analytics";
import { getCustomerToken, accountPut } from "@/lib/account";

const CartContext = createContext(null);
const STORAGE_KEY = "gt_cart";

// A cart line is unique by SKU + requested ring size + engraving/plate text
// (a sized-to-order or personalized piece is a different item from the same
// piece as-stocked).
export const lineKey = (sku, size, engrave, plate) => `${sku}::${size ?? ""}::${engrave ?? ""}::${plate ?? ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // P3C: server-side recoverable cart — only for logged-in customers (email
  // legitimately known). Debounced; powers the opt-in-only abandoned-cart
  // reminders. Token ties a paid checkout back to the recovered cart.
  const syncTimer = useRef(null);
  const prevCount = useRef(null);
  useEffect(() => {
    if (!getCustomerToken()) return undefined;
    // Never push an EMPTY cart on fresh mount: on a second device the local
    // cart starts empty while the server still holds the saved one — pushing
    // [] would erase it and break the emailed recovery link. An empty push is
    // only sent when the user actually removed the last item this session.
    const isFreshEmptyMount = items.length === 0 && (prevCount.current === null || prevCount.current === 0);
    prevCount.current = items.length;
    if (isFreshEmptyMount) return undefined;
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      accountPut("/account/cart", {
        items: items.map((i) => ({
          sku: i.sku,
          quantity: i.quantity,
          ...(i.size != null ? { size: i.size } : {}),
          ...(i.engrave ? { engrave: i.engrave } : {}),
          ...(i.plate ? { plate: i.plate } : {}),
          ...(i.size_label ? { size_label: i.size_label } : {}),
        })),
      })
        .then((d) => {
          if (d?.token) localStorage.setItem("gt_cart_token", d.token);
          else localStorage.removeItem("gt_cart_token");
        })
        .catch(() => {});
    }, 1500);
    return () => clearTimeout(syncTimer.current);
  }, [items]);

  const add = (product, quantity = 1, sizing = null, engraving = null, plate = null, sizeLabel = null) => {
    trackAddToCart(product, quantity);
    setItems((prev) => {
      const key = lineKey(product.sku, sizing?.size, engraving?.text, plate);
      const existing = prev.find((i) => lineKey(i.sku, i.size, i.engrave, i.plate) === key);
      if (existing) {
        return prev.map((i) =>
          lineKey(i.sku, i.size, i.engrave, i.plate) === key ? { ...i, quantity: Math.min(10, i.quantity + quantity) } : i
        );
      }
      return [
        ...prev,
        {
          sku: product.sku,
          name: displayTitle(product),
          price: product.retail_price,
          image: product.image_url,
          quantity,
          ...(sizing ? { size: sizing.size, sizing_fee: sizing.fee } : {}),
          ...(engraving ? { engrave: engraving.text, engraving_fee: engraving.fee } : {}),
          ...(plate ? { plate } : {}),
          ...(sizeLabel ? { size_label: sizeLabel } : {}),
        },
      ];
    });
  };

  const update = (sku, quantity, size = null, engrave = null, plate = null) => {
    if (quantity <= 0) {
      const target = items.find((i) => lineKey(i.sku, i.size, i.engrave, i.plate) === lineKey(sku, size, engrave, plate));
      if (target) trackRemoveFromCart(target);
    }
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => lineKey(i.sku, i.size, i.engrave, i.plate) !== lineKey(sku, size, engrave, plate))
        : prev.map((i) =>
            lineKey(i.sku, i.size, i.engrave, i.plate) === lineKey(sku, size, engrave, plate)
              ? { ...i, quantity: Math.min(10, quantity) }
              : i
          )
    );
  };

  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const lineTotal = (i) => (i.price + (i.sizing_fee || 0) + (i.engraving_fee || 0)) * i.quantity;
  const subtotal = items.reduce((s, i) => s + lineTotal(i), 0);

  return (
    <CartContext.Provider value={{ items, add, update, clear, count, subtotal, lineTotal }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
